import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/testIds";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function CoachEditPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ full_name: "", phone_number: "", password: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        const coaches = await api.listCoaches();
        const c = coaches.find((c) => c.id === id);
        if (!c) {
          toast.error("Coach not found");
          nav("/coaches");
          return;
        }
        setForm({ full_name: c.full_name, phone_number: c.phone_number, password: "" });
      } catch (e) {
        toast.error(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone_number.trim()) {
      toast.error("Name and phone number are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { full_name: form.full_name.trim(), phone_number: form.phone_number.trim() };
      if (form.password) payload.password = form.password;
      await api.updateCoach(id, payload);
      toast.success("Coach updated");
      nav("/coaches");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-16 text-slate-500">Loading...</div>;

  return (
    <div className="max-w-md mx-auto space-y-6">
      <Link to="/coaches" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" />
        Back to coaches
      </Link>
      <h1 className="text-3xl font-extrabold text-slate-900">Edit Coach</h1>

      <form onSubmit={submit} className="space-y-4 bg-slate-50 border border-slate-100 rounded-xl p-6">
        <label className="block">
          <span className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Full Name</span>
          <input
            data-testid={TID.coachNameInput}
            value={form.full_name}
            onChange={(e) => set("full_name", e.target.value)}
            className="w-full h-11 px-3 border border-slate-200 rounded-lg bg-white outline-none focus:border-[#0A2540]"
            required
          />
        </label>
        <label className="block">
          <span className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">Phone Number</span>
          <input
            data-testid={TID.coachPhoneInput}
            value={form.phone_number}
            onChange={(e) => set("phone_number", e.target.value)}
            placeholder="+91 ..."
            className="w-full h-11 px-3 border border-slate-200 rounded-lg bg-white outline-none focus:border-[#0A2540]"
            required
          />
        </label>
        <label className="block">
          <span className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">New Password (optional)</span>
          <input
            data-testid={TID.coachPasswordInput}
            type="password"
            value={form.password}
            onChange={(e) => set("password", e.target.value)}
            placeholder="Leave blank to keep current password"
            className="w-full h-11 px-3 border border-slate-200 rounded-lg bg-white outline-none focus:border-[#0A2540]"
          />
          <span className="block text-xs text-slate-500 mt-1">Only fill this in if you want to reset the coach's password.</span>
        </label>
        <button
          type="submit"
          data-testid={TID.coachSaveBtn}
          disabled={saving}
          className="w-full h-12 bg-[#0A2540] hover:bg-[#081D33] disabled:opacity-60 text-white rounded-lg font-semibold"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </form>
    </div>
  );
}
